import React, { createContext, useContext, useEffect, useState } from "react";

import { isStandaloneDisplay } from "../pwa";
import { setDisplayTimezone } from "../utils/datetime";
import axios from "axios";

/**
 * Runtime branding. Fetched from the unauthenticated /branding/public
 * endpoint at app boot so login screens can render with the MSP's colors,
 * logo, and contact info BEFORE the user has credentials.
 *
 * The fetched brand_color is injected into :root as --brand-primary so the
 * Tailwind `brand` color (configured in tailwind.config.js) resolves to it.
 * That means every component using `bg-brand`, `text-brand`, etc. re-skins
 * automatically when an admin changes the color in Settings.
 *
 * Defaults below are the AeroGauge product brand, used by a fresh
 * appliance with no DB rows. An MSP can white-label any of these in
 * Settings → Branding (the DB rows override these defaults).
 */

export type Branding = {
  company_name: string;
  brand_color: string;
  brand_color_dark: string;
  support_email: string;
  /** Customer-facing support address. The portal audience is the
   *  MSP's CUSTOMERS, so this is deliberately not support_email,
   *  which points at whoever supports the MSP. */
  portal_support_email: string;
  console_eyebrow: string;
  portal_eyebrow: string;
  email_tagline: string;
  has_logo: boolean;
  logo_url: string | null;
  demo_mode: boolean;
  display_timezone: string;
};

const DEFAULTS: Branding = {
  company_name: "AeroGauge",
  brand_color: "#1e3a5f",
  brand_color_dark: "#162d49",
  // Product default. Overridable per install in Settings → Branding; must not
  // be any single reseller's domain.
  support_email: "support@aerogauge.net",
  portal_support_email: "support@aerogauge.net",
  console_eyebrow: "Operations Console",
  portal_eyebrow: "Customer Portal",
  email_tagline: "Managed IT • Monitoring • Security",
  has_logo: false,
  logo_url: null,
  demo_mode: false,
  // EMPTY, not "UTC". This default only applies when the API is unreachable,
  // and in that case "I don't know the operator's zone yet" must fall through
  // to the browser's zone — hardcoding "UTC" here renders every timestamp on
  // the appliance four hours out and looks exactly like the bug this was
  // meant to prevent. The backend sends the real configured zone.
  display_timezone: "",
};

// The AeroGauge product logo bundled in /public. Used as the default brand
// mark whenever no MSP logo has been uploaded, so the product is logo-branded
// everywhere (login, sidebar, portal) out of the box. An uploaded logo (served
// from the API) always wins.
const DEFAULT_LOGO_URL = "/aerogauge.png";

// The PRODUCT's own name, as distinct from whoever's install this is. Used for
// the browser tab and anywhere the product must identify itself rather than
// the operator — see the white-label rules in services/email_service.py.
export const PRODUCT_NAME = "AeroGauge";

function withDefaultLogo(b: Branding): Branding {
  if (b.has_logo && b.logo_url) return b;
  return { ...b, has_logo: true, logo_url: DEFAULT_LOGO_URL };
}

type BrandingCtx = Branding & { refresh: () => Promise<void> };

const BrandingContext = createContext<BrandingCtx>({
  ...DEFAULTS,
  refresh: async () => {},
});

function hexToRgbTriplet(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 0xff} ${(n >> 8) & 0xff} ${n & 0xff}`;
}

function darken(hex: string, amount = 0.18): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((n & 0xff) * (1 - amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/** Relative luminance, WCAG's definition. Used to decide how far a brand
 *  colour has to move before it is readable on a dark surface. */
function luminance(r: number, g: number, b: number): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === R) h = ((G - B) / d + (G < B ? 6 : 0)) / 6;
  else if (max === G) h = ((B - R) / d + 2) / 6;
  else h = ((R - G) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const chan = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(chan(h + 1 / 3) * 255),
    Math.round(chan(h) * 255),
    Math.round(chan(h - 1 / 3) * 255),
  ];
}

/** A version of the brand colour that can be READ on a dark background.
 *
 * The default brand is #1e3a5f, a dark navy. On a light card it is a fine
 * link colour; on `dark:bg-gray-800` it is very nearly invisible, and the
 * console used bare `text-brand` for links on both. Hardcoding blue would fix
 * the legibility and throw the white-label away, so the hue is kept and only
 * the lightness moves. An MSP whose brand is already bright gets it back
 * essentially unchanged.
 */
function brandOnDark(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const [r0, g0, b0] = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];

  // IN HSL, NOT TOWARD WHITE. Blending a colour toward white raises its
  // luminance by desaturating it: #1e3a5f came out #aeb8c6, a readable grey
  // with the brand drained out of it. Raising LIGHTNESS while holding hue and
  // saturation gives a colour that is both readable and recognisably theirs.
  const [h, s, l0] = rgbToHsl(r0, g0, b0);
  // Give a muted brand enough saturation to read as a link - but leave a
  // genuinely achromatic one alone. Forcing saturation onto black produced a
  // pink link, because hue is arbitrarily 0 when there is no colour.
  const sat = s < 0.05 ? 0 : Math.max(s, 0.35);
  // Contrast target against the theme's darkest surface (#111827).
  const TARGET = 0.35;
  let [r, g, b] = [r0, g0, b0];
  for (let l = Math.max(l0, 0.55); l <= 0.9; l += 0.02) {
    [r, g, b] = hslToRgb(h, sat, l);
    if (luminance(r, g, b) >= TARGET) break;
  }
  return `${r} ${g} ${b}`;
}

function applyToRoot(b: Branding) {
  const root = document.documentElement;
  // Tailwind brand color is `rgb(var(--brand-primary) / <alpha-value>)`, so
  // the variable must hold an RGB triplet, not a hex code, for the alpha
  // modifier (`bg-brand/30`, `ring-brand/50`) to keep working.
  const primary = hexToRgbTriplet(b.brand_color) || "30 58 95";
  const primaryDark =
    hexToRgbTriplet(b.brand_color_dark || darken(b.brand_color)) || "22 45 73";
  root.style.setProperty("--brand-primary", primary);
  root.style.setProperty("--brand-primary-dark", primaryDark);
  // Readable on a dark surface, hue preserved. See brandOnDark.
  root.style.setProperty("--brand-on-dark", brandOnDark(b.brand_color) || "125 168 216");
  // Browser tab: PRODUCT first, then whose install it is — "AeroGauge —
  // Acme IT". The word "RMM" is deliberately absent everywhere in the chrome:
  // it read as "AeroGauge RMM - DCNS, LLC RMM", saying the same thing twice
  // and appending a category noun to a company name that does not want one.
  // Only override the default — page-specific titles set by callers stick.
  // The two old defaults are listed so a browser holding a cached copy of
  // either shell still gets retitled.
  if (
    document.title === "AeroGauge RMM" ||
    document.title === "AeroGauge" ||
    document.title === "AeroGauge Remote Monitoring & Management" ||
    document.title === "DCNS RMM" ||
    document.title === "React App"
  ) {
    const company = (b.company_name || "").trim();
    const named = company && company !== PRODUCT_NAME;
    // In an INSTALLED PWA the window chrome already prepends the manifest
    // name, so repeating it here produced "AeroGauge RMM - AeroGauge - DCNS,
    // LLC" in the title bar - the product named twice. Standalone gets the
    // company only, and the chrome supplies "AeroGauge - "; a browser tab has
    // no such chrome, so it carries the full string itself. Either way the
    // user reads exactly "AeroGauge - <company>".
    if (isStandaloneDisplay()) {
      document.title = named ? company : PRODUCT_NAME;
    } else {
      document.title = named ? `${PRODUCT_NAME} - ${company}` : PRODUCT_NAME;
    }
  }
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<Branding>(withDefaultLogo(DEFAULTS));

  const refresh = React.useCallback(async () => {
    try {
      const r = await axios.get<Branding>("/api/v1/settings/branding/public");
      const merged = withDefaultLogo({ ...DEFAULTS, ...r.data });
      setBranding(merged);
      applyToRoot(merged);
      // Publish the operator's clock to the formatting helpers, so every
      // timestamp honours Settings -> Date & Time without each of the 56
      // files that render one having to remember to ask for it.
      setDisplayTimezone(merged.display_timezone);
    } catch {
      /* Backend unreachable — defaults stay applied. */
    }
  }, []);

  useEffect(() => {
    // Apply defaults synchronously so the first paint has the right color
    // even before the fetch resolves.
    applyToRoot(DEFAULTS);
    refresh();
  }, [refresh]);

  return (
    <BrandingContext.Provider value={{ ...branding, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingCtx {
  return useContext(BrandingContext);
}
